import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createConversation,
  getConversation,
  updateConversation,
  addMessageBatch,
  loadMessages,
  setThreadId as persistThreadId,
  setNhConversationId as persistNhConversationId,
} from '../services/conversationService';
import { generateTitle } from '../services/titleGenerationService';

/**
 * Hook that manages conversation persistence.
 * Wraps around existing chatScreen state to add Firestore backing.
 *
 * @param {object} opts
 * @param {object|null} opts.user - Firebase auth user
 * @param {string} opts.chatbotType - e.g. "nurAlHuda"
 * @param {string} opts.assistantTitle - e.g. "Nur Al Huda"
 * @param {string|undefined} opts.conversationIdParam - from URL params
 * @param {string|null} opts.resourceId - PaliGPT resource
 */
export const useConversation = ({
  user,
  chatbotType,
  assistantTitle,
  conversationIdParam,
  resourceId = null,
}) => {
  const navigate = useNavigate();
  const [conversationId, setConversationId] = useState(conversationIdParam || null);
  const [conversationTitle, setConversationTitle] = useState('New Conversation');
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const titleGeneratedRef = useRef(false);
  const messageCountRef = useRef(0);

  // Load conversation from URL param on mount
  useEffect(() => {
    if (!user || !conversationIdParam) return;

    let cancelled = false;
    setIsLoadingConversation(true);

    (async () => {
      try {
        const conv = await getConversation(user.uid, conversationIdParam);
        if (cancelled || !conv) {
          setIsLoadingConversation(false);
          return;
        }
        setConversationId(conv.id);
        setConversationTitle(conv.title || 'New Conversation');
        titleGeneratedRef.current = conv.titleGenerated || false;
        messageCountRef.current = conv.messageCount || 0;

        // Return conversation data so chatScreen can restore state
        return conv;
      } catch (err) {
        console.error('Error loading conversation:', err);
      } finally {
        if (!cancelled) setIsLoadingConversation(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, conversationIdParam]);

  /**
   * Load messages for a conversation (called by chatScreen after hook loads conv).
   */
  const loadConversationMessages = useCallback(async (convId) => {
    if (!user) return { messages: [], conv: null };
    const conv = await getConversation(user.uid, convId || conversationId);
    if (!conv) return { messages: [], conv: null };

    const msgs = await loadMessages(user.uid, conv.id);
    // Convert Firestore messages to chatScreen format
    const formatted = msgs.map((m) => ({
      sender: m.sender,
      text: m.text,
      images: m.images || [],
      attachments: m.attachments || [],
    }));

    setConversationTitle(conv.title || 'New Conversation');
    titleGeneratedRef.current = conv.titleGenerated || false;
    messageCountRef.current = conv.messageCount || 0;

    return { messages: formatted, conv };
  }, [user, conversationId]);

  /**
   * Persist a completed exchange (user msg + assistant response) to Firestore.
   * Called from chatScreen after finalizeAssistantStream.
   */
  const persistExchange = useCallback(async ({
    userText,
    assistantText,
    images = [],
    attachments = [],
    threadId = null,
    nhConversationId = null,
  }) => {
    if (!user) return null;

    let convId = conversationId;

    // Create conversation on first exchange
    if (!convId) {
      const apiPath = chatbotType === 'nurAlHuda' ? 'responses' : 'assistants';
      convId = await createConversation(user.uid, {
        assistantType: chatbotType,
        assistantTitle,
        apiPath,
        resourceId,
      });
      setConversationId(convId);
      // Update URL without reload
      navigate(`/chat/${chatbotType}/${convId}`, { replace: true });
    }

    // Batch write user + assistant messages
    const messagesToSave = [
      { sender: 'user', text: userText, attachments },
      { sender: 'assistant', text: assistantText, images },
    ];
    await addMessageBatch(user.uid, convId, messagesToSave);
    messageCountRef.current += 2;

    // Persist API state
    if (threadId) {
      await persistThreadId(user.uid, convId, threadId);
    }
    if (nhConversationId) {
      await persistNhConversationId(user.uid, convId, nhConversationId);
    }

    // Trigger title generation after first exchange
    if (!titleGeneratedRef.current && messageCountRef.current >= 2) {
      titleGeneratedRef.current = true; // prevent duplicate triggers
      setTimeout(async () => {
        try {
          const title = await generateTitle(userText, assistantText);
          if (title) {
            const { updateTitle } = await import('../services/conversationService');
            await updateTitle(user.uid, convId, title);
            setConversationTitle(title);
          }
        } catch (err) {
          console.error('Title generation failed:', err);
          // Non-critical — title stays as "New Conversation"
        }
      }, 2000);
    }

    return convId;
  }, [user, conversationId, chatbotType, assistantTitle, resourceId, navigate]);

  /**
   * Rename conversation title.
   */
  const renameConversation = useCallback(async (newTitle) => {
    if (!user || !conversationId) return;
    const { updateTitle } = await import('../services/conversationService');
    await updateTitle(user.uid, conversationId, newTitle);
    setConversationTitle(newTitle);
  }, [user, conversationId]);

  /**
   * Reset for a new conversation (called on "New Chat").
   */
  const resetConversation = useCallback(() => {
    setConversationId(null);
    setConversationTitle('New Conversation');
    titleGeneratedRef.current = false;
    messageCountRef.current = 0;
    navigate(`/chat/${chatbotType}`, { replace: true });
  }, [chatbotType, navigate]);

  return {
    conversationId,
    conversationTitle,
    isLoadingConversation,
    persistExchange,
    loadConversationMessages,
    renameConversation,
    resetConversation,
    setConversationTitle,
  };
};
